import { createRouter, createWebHistory } from 'vue-router'
import { adminRoutes } from './menus/admin.js'
import { bookRoutes } from './menus/book.js'
import { landingRoutes } from './menus/landing.js'
import { userRoutes } from './menus/user.js'

const routes = [
  // 랜딩 관련 페이지들
  ...landingRoutes,

  // 앱 홈 페이지 (메인 레이아웃 사용)
  {
    path: '/app',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: { isFullScreen: false }
  },
  
  // 책 관련 페이지들
  ...bookRoutes,
  
  // 사용자 관련 페이지들
  ...userRoutes,
  
  // 관리자 페이지들
  ...adminRoutes,
  
  // 404 페이지
  {
    path: '/:pathMatch(.*)*',
    component: () => import('../views/NotFound.vue')
  }
]

const router = createRouter({
  mode: 'history',
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 브라우저의 뒤로/앞으로 버튼 사용 시 이전 스크롤 위치로
    if (savedPosition) {
      return savedPosition
    }
    // 앵커 링크가 있는 경우
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    // 기본적으로 페이지 최상단으로
    return { top: 0, behavior: 'smooth' }
  }
})

// 라우터 가드 추가
router.beforeEach(async (to, from, next) => {
  const { useAuthStore } = await import('@/stores/auth')
  const authStore = useAuthStore()

  // Firebase Auth 상태 확인 (홈·보호 라우트 공통)
  if (to.path === '/' || to.path === '/app' || to.matched.some((record) => record.meta.requiresAuth)) {
    if (!authStore.user) {
      await authStore.initAuth()
    }
  }

  const { shouldRedirectGuestFromHome } = await import('@/composables/useGuestAccess')

  // 로그인 사용자 → 랜딩 대신 홈
  if ((to.path === '/' || to.path === '/landing') && authStore.isAuthenticated) {
    next('/app')
    return
  }

  // 인증이 필요한 라우트
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      next({ path: '/', query: { redirect: to.fullPath } })
      return
    }

    if (to.matched.some(record => record.meta.requiresAdmin)) {
      if (!authStore.isAdmin) {
        next('/app')
        return
      }
    }
  }

  // 비로그인 + 앱 홈 첫 진입 → 랜딩
  if (to.path === '/app' && !authStore.isAuthenticated && shouldRedirectGuestFromHome()) {
    next('/')
    return
  }

  next()
})

// SEO 메타 태그 설정 (라우트 변경 후)
router.afterEach((to) => {
  // 가이드·공개 프로필은 컴포넌트에서 동적 메타를 설정한다.
  // afterEach의 기본값으로 덮어쓰면 prerender/스니펫 품질이 깨진다.
  if (to.name === 'public-profile' || to.name === 'guide' || to.name === 'guides') return

  import('@vueuse/head').then(({ useHead }) => {
    const BASE_URL = 'https://bbitbbitbook.com'
    const OG_IMAGE = `${BASE_URL}/og-image.png`
    const defaultTitle = '삐삐북 - 독서 기록 앱 | 서재·통계·출석·독서통장'
    const defaultDescription =
      '삐삐북은 읽은 책·읽는 중·읽을 책을 관리하는 무료 독서 기록 앱입니다. 독서 통계, 출석체크 캐롯 보상, 독서통장, 공개 프로필 공유를 iOS·웹에서 이용하세요.'

    // 라우트별 SEO 설정 (title, description, noindex 여부)
    const seoConfig = {
      '/': {
        title: defaultTitle,
        description: defaultDescription,
        keywords:
          '독서기록앱,독서앱,책관리앱,독서통계,독서습관,독서노트,독서통장,출석체크,삐삐북,bbitbbitbook',
      },
      '/app': {
        title: '삐삐북 - 내 독서 홈',
        description: '삐삐북 앱 홈에서 오늘의 독서 기록과 추천 동선을 확인하세요.',
        noindex: true,
      },
      '/app/library': {
        title: '내 서재 - 삐삐북',
        description: '내가 읽은 책, 읽고 있는 책, 읽을 책을 한눈에 관리하세요.',
        noindex: true,
      },
      '/app/stats': {
        title: '독서 통계 - 삐삐북',
        description: '나의 독서 통계와 월별 완독 현황, 장르 분포, 별점 분석을 확인하세요.',
        noindex: true,
      },
      '/app/reading-calendar': {
        title: '독서 달력 - 삐삐북',
        description: '책별 독서 기간을 달력과 타임라인으로 한눈에 확인하세요.',
        noindex: true,
      },
      '/app/profile': {
        title: '내 프로필 - 삐삐북',
        description: '내 독서 프로필을 확인하고 친구들과 공유하세요.',
        noindex: true,
      },
      '/app/settings': {
        title: '설정 - 삐삐북',
        description: '삐삐북 서비스 설정을 관리하세요.',
        noindex: true,
      },
      '/app/memo': {
        title: '독서 메모 - 삐삐북',
        description: '책을 읽으며 남긴 메모와 독서 노트를 관리하세요.',
        noindex: true,
      },
      '/app/book-search': {
        title: '책 찾기 - 삐삐북',
        description: '카카오, 네이버, 알라딘, 국립중앙도서관에서 원하는 책을 검색하고 내 서재에 추가하세요.',
        noindex: true,
      },
      '/app/bankbook': {
        title: '독서통장 - 삐삐북',
        description: '완독한 책들을 독서통장에 기록하고 나만의 독서 이력을 만들어가세요.',
        noindex: true,
      },
      '/terms': {
        title: '서비스 이용약관 - 삐삐북',
        description: '삐삐북 서비스 이용약관을 확인하세요.',
      },
      '/service': {
        title: '개인정보처리방침 - 삐삐북',
        description: '삐삐북 개인정보처리방침을 확인하세요.',
      },
      '/license': {
        title: '오픈소스 라이선스 - 삐삐북',
        description: '삐삐북에서 사용하는 오픈소스 라이선스 정보를 확인하세요.',
      },
      '/notices': {
        title: '공지사항 - 삐삐북',
        description: '삐삐북 서비스 공지사항을 확인하세요.',
      },
      '/guide': {
        title: '독서 가이드 - 삐삐북',
        description:
          '독서 기록 앱 고르는 법, 습관 만들기, 통계 읽기, 메모, 독서통장, 읽을 책 관리를 정리한 공개 가이드입니다.',
      },
    }

    const isAppRoute = to.path === '/app' || to.path.startsWith('/app/')
    const config = seoConfig[to.path] || {
      title: to.meta?.title || defaultTitle,
      description: to.meta?.description || defaultDescription,
      // 책 상세·메모 작성·관리자 등 seoConfig에 없는 앱 경로도 색인 제외
      noindex: isAppRoute,
    }

    const fullUrl = `${BASE_URL}${to.path}`

    const metaTags = [
      { name: 'description', content: config.description },
      ...(config.keywords ? [{ name: 'keywords', content: config.keywords }] : []),
      { property: 'og:site_name', content: '삐삐북' },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: 'ko_KR' },
      { property: 'og:title', content: config.title },
      { property: 'og:description', content: config.description },
      { property: 'og:url', content: fullUrl },
      { property: 'og:image', content: OG_IMAGE },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: config.title },
      { name: 'twitter:description', content: config.description },
      { name: 'twitter:image', content: OG_IMAGE },
    ]

    // 로그인 필요 페이지는 검색 엔진 색인 제외
    if (config.noindex) {
      metaTags.push({ name: 'robots', content: 'noindex, nofollow' })
    } else {
      metaTags.push({ name: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large' })
    }

    useHead({
      title: config.title,
      meta: metaTags,
      link: [
        { rel: 'canonical', href: fullUrl },
      ],
    })
  })
})

/** GA4 / Firebase Analytics — SPA 화면 전환 */
const analyticsPageTitles = {
  '/': '삐삐북 - 독서 기록 앱 | 서재·통계·출석·독서통장',
  '/app': '삐삐북 - 내 독서 홈',
  '/app/library': '내 서재 - 삐삐북',
  '/app/stats': '독서 통계 - 삐삐북',
  '/app/reading-calendar': '독서 달력 - 삐삐북',
  '/app/profile': '내 프로필 - 삐삐북',
  '/app/settings': '설정 - 삐삐북',
  '/app/memo': '독서 메모 - 삐삐북',
  '/app/book-search': '책 찾기 - 삐삐북',
  '/app/bankbook': '독서통장 - 삐삐북',
  '/terms': '서비스 이용약관 - 삐삐북',
  '/service': '개인정보처리방침 - 삐삐북',
  '/license': '오픈소스 라이선스 - 삐삐북',
  '/notices': '공지사항 - 삐삐북',
  '/guide': '독서 가이드 - 삐삐북',
  '/guide/reading-record-app': '독서 기록 앱 추천 - 삐삐북',
  '/guide/reading-habit': '독서 습관 만드는 방법 - 삐삐북',
  '/guide/reading-stats': '독서 통계 가이드 - 삐삐북',
  '/guide/reading-memo': '독서 메모 남기는 법 - 삐삐북',
  '/guide/reading-bankbook': '독서통장이란 - 삐삐북',
  '/guide/book-wishlist': '읽을 책 관리하는 법 - 삐삐북',
}

router.afterEach((to) => {
  let title = analyticsPageTitles[to.path] || to.meta?.title
  if (!title && to.name === 'public-profile') title = '공개 프로필 - 삐삐북'
  if (!title && to.path.startsWith('/app/book/')) title = '책 상세 - 삐삐북'
  if (!title && to.path.includes('/memo/add')) title = '메모 작성 - 삐삐북'
  if (!title && to.name === 'admin') title = '관리자 - 삐삐북'
  if (!title && to.name === 'notice-detail') title = '공지 상세 - 삐삐북'
  import('@/plugins/analytics').then(({ trackPageView }) => {
    trackPageView(to.fullPath, title || document.title || '삐삐북')
  })
})

export default router
